"use client";

import { ColumnDef } from "@tanstack/react-table";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import DriverCreate from "./Create";
import { Badge } from "@/components/ui/badge";
import DialogConfirmCommon from "@/components/common/DialogConfirm";
import { User, Vehicle } from "@/interface/entityInterface";
import { tableCol } from "@/components/common/columns/columns";
import DriverEdit from "./Edit";
import { useEffect, useState } from "react";
import axiosInstance from "@/utils/axiosInstance";
import Actions from "@/components/data-table/Actions";
import { Input } from "@/components/ui/input";
import CompleteOrderItem from "./components/CompleteOrderItem";
import { useUserContext } from "@/context/UserContext";

const DriversList = () => {
  const { decodedToken } = useUserContext();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [driverName, setDriverName] = useState("");

  useEffect(() => {
    const getVehicleList = async () => {
      const { data } = await axiosInstance.get("/vehicles", {
        params: {
          start: 1,
          end: 100,
        },
      });

      setVehicles(data);
    };

    getVehicleList();
  }, []);

  const columns: ColumnDef<User>[] = tableCol({
    showActions: true,
    columns: [
      {
        accessorKey: "firstname",
        header: "Full name",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">
                {row.getValue("firstname")} {row.original.lastname}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("email")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "phone",
        header: "Phone number",
        cell: ({ row }) => {
          const value: any = row.original;
          return (
            <a href={`tel:${value.drivers[0].phone}`}>
              {value.drivers[0].phone}
            </a>
          );
        },
      },
      {
        id: "vehicle",
        accessorKey: "vehicle",
        header: "Vehicle assigned",
        cell: ({ row }) => {
          const value: any = row.original;
          const { vehicle, id } = value.drivers[0];
          return (
            <DialogConfirmCommon
              id={id || null}
              selectedValue={vehicle?.id || null}
              endpoint="drivers"
              description="Assign this vehicle to this driver?"
              propKey="vehicle_id"
              dataProp="nickname"
              endpoint2="assign-vehicle"
              dataOptions={vehicles}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const value: any = row.original;
          return <Badge variant="outline">{value.drivers[0].status}</Badge>;
        },
      },
    ],
    resource: "drivers",
    EditComponent: DriverEdit,
    responseMessage: "Driver status changed to inactive",
    id: (row: User) => {
      return row.drivers[0].id;
    },
  });

  const renderToolbarFilters = (table: any) => (
    <div className="flex items-center gap-x-2">
      {table.getSelectedRowModel().rows.length > 0 &&
        decodedToken?.settings.settings_type === "taxi" && (
          <Actions
            dropdownItems={[
              {
                Component: (
                  <CompleteOrderItem
                    selectedRows={table.getSelectedRowModel().rows}
                  />
                ),
              },
            ]}
          />
        )}
      <Input
        placeholder={`Filter by full name`}
        onChange={(e: React.FormEvent<HTMLInputElement>) =>
          setDriverName(e.currentTarget.value)
        }
        className="w-[220px] lg:w-[250px]"
      />
    </div>
  );

  return (
    <>
      <CommonList
        filters={{
          name: driverName,
        }}
      >
        <CommonTableList
          columns={columns}
          create={<DriverCreate />}
          filterSearch="firstname"
          renderToolbarFilters={renderToolbarFilters}
        />
      </CommonList>
    </>
  );
};

export default DriversList;
